import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Minus, Undo2, Redo2, Eraser, Code2
} from 'lucide-react';
import apiService from '../../services/api';

interface Props {
  value: string;
  onChange: (html: string) => void;
  /** Fichas que se insertan en el punto donde está el cursor. */
  variables?: Record<string, string>;
  minHeight?: number;
}

/**
 * Editor visual del correo. El administrador escribe como en un procesador de
 * texto —negritas, listas, imágenes— y nunca ve etiquetas HTML; el HTML final
 * se emite hacia arriba con onChange.
 *
 * Está hecho sobre contentEditable, sin librerías: así no cambia el build ni
 * arrastra dependencias nuevas al proyecto.
 */
const RichTextEditor: React.FC<Props> = ({ value, onChange, variables = {}, minHeight = 320 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastEmitted = useRef(value);
  const savedRange = useRef<Range | null>(null);

  const [showSource, setShowSource] = useState(false);
  const [selectedImg, setSelectedImg] = useState<HTMLImageElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Solo se reescribe el contenido cuando el cambio viene de fuera (p. ej. al
  // aplicar una plantilla); si no, el cursor saltaría en cada tecla.
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastEmitted.current) {
      ref.current.innerHTML = value || '';
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      ref.current?.focus();
    }
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertHtml = (html: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand('insertHTML', false, html);
    emit();
  };

  // --- imágenes -----------------------------------------------------------

  const uploadImage = async (file: File) => {
    setError('');
    if (!file.type.startsWith('image/')) { setError('Ese archivo no es una imagen'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('La imagen pesa más de 5 MB'); return; }

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiService.post('/admin/email/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data?.url;
      if (!url) throw new Error('sin url');
      insertHtml(
        `<img src="${url}" alt="" style="max-width:100%;width:100%;height:auto;display:block;margin:16px 0;border-radius:6px;" />`
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const image = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
    if (image) {
      const file = image.getAsFile();
      if (file) { e.preventDefault(); uploadImage(file); return; }
    }
    // El texto pegado entra sin estilos ajenos (Word, Google Docs…), que suelen
    // romper el diseño del correo.
    const html = e.clipboardData.getData('text/html');
    if (html) {
      e.preventDefault();
      insertHtml(sanitize(html));
    }
  };

  const onDrop = (e: React.DragEvent) => {
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      e.preventDefault();
      uploadImage(file);
    }
  };

  const setImageWidth = (pct: number) => {
    if (!selectedImg) return;
    selectedImg.style.width = `${pct}%`;
    selectedImg.style.maxWidth = '100%';
    selectedImg.style.marginLeft = pct === 100 ? '0' : 'auto';
    selectedImg.style.marginRight = pct === 100 ? '0' : 'auto';
    emit();
  };

  const onClickBody = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    setSelectedImg(target.tagName === 'IMG' ? (target as HTMLImageElement) : null);
    rememberSelection();
  };

  // --- enlaces y bloques ---------------------------------------------------

  const addLink = () => {
    rememberSelection();
    const url = window.prompt('¿A qué dirección debe llevar el enlace?', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const setBlock = (tag: string) => exec('formatBlock', tag);

  return (
    <div className="rte">
      <div className="rte-toolbar" onMouseDown={e => e.preventDefault()}>
        <select className="rte-select" defaultValue="p" onChange={e => setBlock(e.target.value)} title="Estilo de texto">
          <option value="p">Párrafo</option>
          <option value="h2">Título</option>
          <option value="h3">Subtítulo</option>
          <option value="blockquote">Cita</option>
        </select>

        <span className="rte-sep" />
        <button type="button" className="rte-btn" onClick={() => exec('bold')} title="Negrita"><Bold size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('italic')} title="Cursiva"><Italic size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('underline')} title="Subrayado"><Underline size={15} /></button>

        <span className="rte-sep" />
        <button type="button" className="rte-btn" onClick={() => exec('insertUnorderedList')} title="Lista"><List size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('insertOrderedList')} title="Lista numerada"><ListOrdered size={15} /></button>

        <span className="rte-sep" />
        <button type="button" className="rte-btn" onClick={() => exec('justifyLeft')} title="Alinear a la izquierda"><AlignLeft size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('justifyCenter')} title="Centrar"><AlignCenter size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('justifyRight')} title="Alinear a la derecha"><AlignRight size={15} /></button>

        <span className="rte-sep" />
        <button type="button" className="rte-btn" onClick={addLink} title="Insertar enlace"><Link2 size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => { rememberSelection(); fileRef.current?.click(); }}
          title="Insertar imagen" disabled={uploading}>
          <ImageIcon size={15} />
        </button>
        <button type="button" className="rte-btn" onClick={() => insertHtml('<hr style="border:none;border-top:1px solid #e5e0d5;margin:22px 0;" />')} title="Separador">
          <Minus size={15} />
        </button>

        <span className="rte-sep" />
        <button type="button" className="rte-btn" onClick={() => exec('undo')} title="Deshacer"><Undo2 size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('redo')} title="Rehacer"><Redo2 size={15} /></button>
        <button type="button" className="rte-btn" onClick={() => exec('removeFormat')} title="Quitar formato"><Eraser size={15} /></button>

        <button type="button" className={`rte-btn rte-btn--right ${showSource ? 'on' : ''}`}
          onClick={() => setShowSource(s => !s)} title="Ver el código HTML (avanzado)">
          <Code2 size={15} />
        </button>
      </div>

      {Object.keys(variables).length > 0 && (
        <div className="rte-vars">
          <span className="rte-vars-label">Datos del destinatario:</span>
          {Object.entries(variables).map(([token, desc]) => (
            <button key={token} type="button" className="rte-var" title={desc}
              onMouseDown={e => { e.preventDefault(); rememberSelection(); }}
              onClick={() => insertHtml(token)}>
              {token.replace(/[{}]/g, '')}
            </button>
          ))}
        </div>
      )}

      {selectedImg && (
        <div className="rte-imgbar">
          <span>Imagen seleccionada:</span>
          <button type="button" onClick={() => setImageWidth(100)}>Ancho completo</button>
          <button type="button" onClick={() => setImageWidth(60)}>Mediana</button>
          <button type="button" onClick={() => setImageWidth(35)}>Pequeña</button>
          <button type="button" className="rte-imgbar-del"
            onClick={() => { selectedImg.remove(); setSelectedImg(null); emit(); }}>
            Quitar
          </button>
        </div>
      )}

      {error && <div className="rte-error">{error}</div>}
      {uploading && <div className="rte-uploading">Subiendo imagen…</div>}

      {showSource ? (
        <textarea
          className="rte-source"
          style={{ minHeight }}
          value={value}
          onChange={e => { lastEmitted.current = e.target.value; onChange(e.target.value); }}
        />
      ) : (
        <div
          ref={ref}
          className="rte-body"
          style={{ minHeight }}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={() => { rememberSelection(); emit(); }}
          onKeyUp={rememberSelection}
          onMouseUp={rememberSelection}
          onClick={onClickBody}
          onPaste={onPaste}
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
        />
      )}

      <input ref={fileRef} type="file" accept="image/*" hidden
        onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ''; }} />
    </div>
  );
};

/** Deja pasar solo las etiquetas que los clientes de correo manejan bien. */
function sanitize(html: string): string {
  const allowed = ['B', 'STRONG', 'I', 'EM', 'U', 'A', 'P', 'BR', 'UL', 'OL', 'LI', 'H2', 'H3', 'BLOCKQUOTE', 'IMG', 'DIV', 'SPAN', 'HR'];
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('*').forEach(el => {
    if (!allowed.includes(el.tagName)) {
      el.replaceWith(...Array.from(el.childNodes));
      return;
    }
    Array.from(el.attributes).forEach(attr => {
      const keep = (el.tagName === 'A' && attr.name === 'href')
        || (el.tagName === 'IMG' && ['src', 'alt', 'style'].includes(attr.name));
      if (!keep) el.removeAttribute(attr.name);
    });
  });

  return doc.body.innerHTML;
}

export default RichTextEditor;
